import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppDispatch } from 'store';
import { getEmptyRoutesConfig } from 'utils';
import * as api from '../api';
import { setError } from './general';

type RegistrationSliceState = {
  routeNumbers?: RouteNumbersConfig;
  clubs?: string[];
  climbers?: RegisteredClimber[];
  judges?: Judge[];
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState: {} as RegistrationSliceState,
  reducers: {
    setRoutesConfig(state, { payload }: PayloadAction<RouteNumbersConfig>) {
      state.routeNumbers = payload;
    },
    setClubs(state, { payload }: PayloadAction<string[]>) {
      state.clubs = payload;
    },
    addClub(state, { payload }: PayloadAction<string>) {
      if (!state.clubs) state.clubs = [];

      state.clubs.push(payload);
    },
    setClimbers(state, { payload }: PayloadAction<RegisteredClimber[]>) {
      state.climbers = payload;
    },
    setJudges(state, { payload }: PayloadAction<Judge[]>) {
      state.judges = payload;
    },
  },
});

const { setRoutesConfig, setClubs, addClub, setClimbers, setJudges } =
  registrationSlice.actions;

export function fetchRoutesConfig() {
  return async function (dispatch: AppDispatch) {
    try {
      const routeNumbers =
        (await api.getRouteNumbers()) || getEmptyRoutesConfig();

      dispatch(setRoutesConfig(routeNumbers));
    } catch (err) {
      dispatch(setError('Failed to fetch routes config'));
      console.error('Failed to fetch routes config', err);
    }
  };
}

export function updateRoutesConfig(config: RouteNumbersConfig) {
  return async function (dispatch: AppDispatch) {
    try {
      const updatedConfig = await api.saveRoutesConfig(config);
      dispatch(setRoutesConfig(updatedConfig));
    } catch (err) {
      dispatch(setError('Failed to save routes config'));
      console.error('Failed to save routes config', err);
    }
  };
}

export function fetchClubs() {
  return async function (dispatch: AppDispatch) {
    try {
      const clubs = await api.getClubs();

      dispatch(setClubs(clubs));
    } catch (err) {
      dispatch(setError('Failed to fetch clubs'));
      console.error('Failed to fetch clubs', err);
    }
  };
}

export function createClub(clubName: string) {
  return async function (dispatch: AppDispatch) {
    try {
      const newClub = await api.addClub(clubName);

      dispatch(addClub(newClub.name));

      return newClub.name;
    } catch (err) {
      dispatch(setError('Failed to create club'));
      console.error('Failed to create club', err);
    }
  };
}

export function fetchClimbers() {
  return async function (dispatch: AppDispatch) {
    try {
      const climbers = await api.fetchClimbers();

      dispatch(setClimbers(climbers));
    } catch (err) {
      dispatch(setError('Failed to fetch climbers'));
      console.error('Failed to fetch climbers', err);
    }
  };
}

export function addClimber(climber: RegisteredClimber) {
  return async function (dispatch: AppDispatch): Promise<boolean> {
    try {
      await api.addClimber(climber);
      return true;
    } catch (err) {
      dispatch(setError('Failed to add climber'));
      console.error('Failed to add climber', err);
      return false;
    }
  };
}

export function updateClimber(climber: RegisteredClimber) {
  return async function (dispatch: AppDispatch) {
    try {
      await api.updateClimber(climber);
    } catch (err) {
      dispatch(setError('Failed to update climber'));
      console.error('Failed to update climber', err);
    }
  };
}

export function fetchJudges() {
  return async function (dispatch: AppDispatch) {
    try {
      const judges = await api.fetchUsers();
      dispatch(setJudges(judges));
    } catch (err) {
      dispatch(setError('Failed to fetch judges'));
      console.error('Failed to fetch judges', err);
    }
  };
}

export default registrationSlice.reducer;
